/*
 * Which build is this?
 *
 * Three facts about the artefact the browser is currently running, read once at
 * module scope. Nothing here calls the API — a build identifies itself from
 * constants baked in at build time, so the answer is available before the user
 * has signed in and cannot disagree with the bundle it is describing.
 *
 * `__APP_BRANCH__` and `__APP_COMMIT__` are injected by `vite.config.ts` via
 * `define`. CI overrides them per build with the `APP_BRANCH` / `APP_COMMIT`
 * environment variables, so a preview bundle carries ITS OWN identity rather
 * than whatever the build agent's HEAD happened to point at.
 */

declare const __APP_BRANCH__: string;
declare const __APP_COMMIT__: string;

/*
 * The `typeof` guards are not defensive padding. `define` performs a textual
 * substitution at build time, so if the constant is ever absent the identifier
 * survives into the bundle and referencing it is a ReferenceError that takes
 * the whole app down at module load — before any error boundary exists to
 * catch it. A missing build constant must degrade to an unlabelled pill, never
 * to a white screen.
 */
export const BUILD_BRANCH: string =
  typeof __APP_BRANCH__ !== 'undefined' ? __APP_BRANCH__ : 'dev';

/**
 * Shorten a full commit sha to the seven characters everything else here uses.
 *
 * ⚠️ **`preview.yml` passes `github.sha`, which is FORTY characters**, while
 * every manifest entry carries seven — `normaliseSha` in
 * `scripts/build-environments-manifest.mjs` slices to seven on that side. So
 * without this the pill renders its own build with a forty-character sha
 * beside seven-character ones in the very same menu, and the label blows out
 * the control's width.
 *
 * ⚠️ **Only a value that LOOKS like a sha is touched.** Anything else is passed
 * through whole: truncating an arbitrary label to seven characters would turn
 * a value somebody chose into a fragment of one, which is worse than showing it
 * long. The accepted range matches `normaliseSha` exactly — seven to forty hex
 * characters — so the two sides cannot disagree about what a sha is.
 */
function shortCommit(value: string): string {
  return /^[0-9a-fA-F]{7,40}$/.test(value) ? value.slice(0, 7).toLowerCase() : value;
}

export const BUILD_COMMIT: string =
  typeof __APP_COMMIT__ !== 'undefined' ? shortCommit(__APP_COMMIT__) : '';

/**
 * Vite's runtime view of `base` — `"/"` on an apex build, `"/b/<slug>/"` on a
 * preview. This is the value a manifest entry's `url` is compared against to
 * decide which entry is the one you are looking at, so the two must agree byte
 * for byte, trailing slash included.
 */
export const BUILD_BASE: string = import.meta.env.BASE_URL;

/**
 * True when this bundle is a per-branch preview rather than the apex build.
 *
 * ⚠️ **Derived from `BUILD_BASE`, deliberately NOT from `BUILD_BRANCH`.** The
 * question this answers is *"is this a preview deployment"*, and only the base
 * path knows that. A branch name cannot: `main` is built for the apex AND could
 * be built as a preview, and a local `npm run dev` on a feature branch is not a
 * preview of anything. Since previews call the PRODUCTION API — the owner's
 * decision, 8 September 2026 — this flag is what raises the writes-are-real
 * warning, and it must never be true on a local dev run pointed at localhost.
 */
export const IS_PREVIEW: boolean = BUILD_BASE !== '/';

/**
 * What `<BrowserRouter basename>` needs, derived from `BUILD_BASE`.
 *
 * 🔴 **WITHOUT THIS EVERY ROUTE ON A PREVIEW IS THE 404 PAGE, AND NOTHING ELSE
 * REPORTS IT.** Vite's `base` prefixes the ASSET urls, so a preview served at
 * `/b/<slug>/` fetches its bundle correctly, boots correctly and logs nothing —
 * and then React Router, which knows nothing about `base`, is handed the path
 * `/b/<slug>/`, matches no route, and renders "Page not found".
 *
 * ⚠️ **MEASURED, not reasoned about.** The first real preview returned HTTP 200
 * with its own bundle, zero console errors and zero failed requests, and showed
 * the 404 page. Every HTTP-level check passed. Only opening it in a browser
 * found it, which is why that step is not optional for this feature.
 *
 * ⚠️ **The trailing slash is stripped, and `BUILD_BASE` is NOT changed.** Those
 * are two different consumers: `BUILD_BASE` is compared byte for byte against a
 * manifest entry's `url`, which carries the trailing slash, while React Router
 * wants a basename without one. Normalising the shared constant to suit the
 * router would silently break the comparison that decides which build the
 * switcher thinks you are looking at.
 */
export const ROUTER_BASENAME: string = BUILD_BASE === '/' ? '/' : BUILD_BASE.replace(/\/+$/, '');

/**
 * True when the running code is not `main`.
 *
 * Separate from `IS_PREVIEW` and answering a different question: *whose code is
 * this*, rather than *where is it deployed*. It colours the pill, where
 * `IS_PREVIEW` raises the banner — a preview of `main` is still `main`'s code,
 * and a local run of a feature branch is still a feature branch.
 *
 * Not `import.meta.env.DEV`: that reports the Vite mode, which is a fact about
 * how the bundle was built rather than about which code is in it.
 */
export const IS_OFF_MAIN: boolean = BUILD_BRANCH !== 'main';
